import type { MessageKey } from './cs';

/** Anglická jazyková sada; klíče musí přesně odpovídat české sadě. */
export const en: Record<MessageKey, string> = {
  'app.title': 'Bits2Schema',
  'app.subtitle': 'Database schema modelling and visualisation',

  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.next': 'Continue',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.open': 'Open',
  'common.create': 'Create',
  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.never': 'never',
  'common.required': 'required',
  'common.unknownError': 'An unexpected error occurred.',

  'projects.title': 'Projects',
  'projects.empty': 'No projects yet. Create one by importing a database, or start empty.',
  'projects.newFromDatabase': 'New project from database',
  'projects.newEmpty': 'New empty project',
  'projects.column.name': 'Name',
  'projects.column.dialect': 'Dialect',
  'projects.column.origin': 'Origin',
  'projects.column.tables': 'Tables',
  'projects.column.lastImport': 'Last import',
  'projects.column.lastOpened': 'Last opened',
  'projects.column.owner': 'Owner',
  'projects.column.actions': 'Actions',
  'projects.origin.import': 'Imported from DB',
  'projects.origin.greenfield': 'Empty project',
  'projects.delete.title': 'Delete project',
  'projects.delete.question':
    'Really delete project “{name}”? The project file and its entire version history will be removed.',
  'projects.delete.irreversible': 'This action is irreversible and cannot be undone.',
  'projects.delete.noDbImpact': 'The target database is not affected — only the local model is removed.',

  'greenfield.title': 'New empty project',
  'greenfield.description':
    'An empty project is created without a database connection — only the SQL dialect is chosen. A connection can be added later at any time.',
  'greenfield.nameLabel': 'Project name',
  'greenfield.dialectLabel': 'SQL dialect',

  'import.title': 'New project from database',
  'import.step.project': 'Project',
  'import.step.connection': 'Connection',
  'import.step.schemas': 'Schemas',
  'import.step.run': 'Import',
  'import.nameLabel': 'Project name',
  'import.dialectLabel': 'SQL dialect',
  'import.testConnection': 'Test connection',
  'import.testing': 'Testing connection…',
  'import.testOk': 'Connection works. Version: {version}',
  'import.testOkNoVersion': 'Connection works.',
  'import.testFailed': 'Connection failed: {message}',
  'import.readOnlyHint': 'Recommendation: use an account with read-only permissions for imports.',
  'import.schemasTitle': 'Select schemas to import',
  'import.schemasHint': 'The whole selected schema is always imported, including all its tables.',
  'import.schemasEmpty': 'The database contains no user schema.',
  'import.schemasLoad': 'Load schemas',
  'import.schemasNotSupported':
    'This dialect does not use schema selection — the whole file is imported.',
  'import.start': 'Start import',
  'import.running': 'Import in progress…',
  'import.progress': 'Processed {done} of {total} tables',
  'import.progressUnknownTotal': 'Processed {done} tables',
  'import.phase.connecting': 'Connecting to the database',
  'import.phase.readingTables': 'Reading tables',
  'import.phase.readingColumns': 'Reading columns',
  'import.phase.readingConstraints': 'Reading constraints',
  'import.phase.readingIndexes': 'Reading indexes',
  'import.phase.buildingModel': 'Building the model',
  'import.phase.savingProject': 'Saving the project',
  'import.phase.done': 'Done',
  'import.failed': 'Import failed: {message}',
  'import.failedNoModel': 'No model was created.',
  'import.succeeded': 'Import finished.',
  'import.openProject': 'Open project',

  'connection.postgres.host': 'Host',
  'connection.postgres.port': 'Port',
  'connection.postgres.database': 'Database',
  'connection.postgres.user': 'User',
  'connection.postgres.password': 'Password',
  'connection.postgres.sslMode': 'SSL mode',
  'connection.postgres.sslMode.disable': 'disable — no encryption',
  'connection.postgres.sslMode.require': 'require — encrypt, do not verify certificate',
  'connection.postgres.sslMode.verifyFull': 'verify-full — verify certificate and hostname',
  'connection.duckdb.file': 'Database file',
  'connection.duckdb.filePlaceholder': 'e.g. analytics/warehouse.duckdb',
  'connection.duckdb.rootHint': 'Allowed directory: {root}',
  'connection.duckdb.noFiles':
    'No DuckDB file found in the allowed directory. The path can also be typed manually.',
  'connection.duckdb.refresh': 'Reload files',
  'connection.passwordNotice':
    'The password is stored encrypted outside the project file and never appears in logs.',

  'warnings.title': 'Import warnings',
  'warnings.none': 'The import finished without problems.',
  'warnings.count': 'Warnings: {count}',
  'warnings.column.location': 'Location',
  'warnings.column.message': 'Description',
  'warnings.code.unknownType': 'Unknown type',
  'warnings.code.permissionDenied': 'Missing permission',
  'warnings.code.tableSkipped': 'Table skipped',
  'warnings.code.indexNotRepresentable': 'Expression index',
  'warnings.code.constraintNotRepresentable': 'Unresolved constraint',
  'warnings.code.metadataQueryFailed': 'Metadata query failed',

  'project.backToList': 'Back to projects',
  'project.dbInfo.button': 'Database info',
  'project.dbInfo.title': 'Database information',
  'project.summary.tables': 'Tables',
  'project.summary.columns': 'Columns',
  'project.summary.relations': 'Relations',
  'project.summary.schemas': 'Schemas',
  'project.connection.title': 'Database connection',
  'project.connection.none': 'This project has no database connection yet.',
  'project.connection.noneHint':
    'Without a connection, actions requiring a live database (synchronisation, pre-flight check before script generation) are unavailable. Schema design and script generation against an empty baseline work without it.',
  'project.connection.attach': 'Add connection',
  'project.connection.attached': 'The connection has been saved.',
  'project.tables.title': 'Imported tables',
  'project.tables.empty': 'The project contains no table yet.',
  'project.tables.column.name': 'Table',
  'project.tables.column.schema': 'Schema',
  'project.tables.column.columns': 'Columns',
  'project.tables.column.pk': 'PK',
  'project.tables.column.fk': 'FK',
  'project.tables.column.indexes': 'Indexes',
  'project.canvasPlaceholder':
    'The project has no tables yet — the canvas will appear once the first table is added.',

  'canvas.saveNow': 'Save',
  'canvas.saveStatus.idle': '',
  'canvas.saveStatus.saving': 'Saving…',
  'canvas.saveStatus.saved': 'Saved',
  'canvas.saveStatus.error': 'Save failed',

  // Epic 13 — undo/redo stack.
  'undo.button': 'Undo',
  'redo.button': 'Redo',
  'undo.tooltip': 'Undo: {description}',
  'redo.tooltip': 'Redo: {description}',

  // Epic 7 — layout algorithms and relationship routing.
  'canvas.autoLayout.button': 'Auto-layout',
  'canvas.autoLayout.confirmTitle': 'Recompute the layout of the whole schema?',
  'canvas.autoLayout.confirmMessage':
    'The positions of all tables and the routes of all relationships will be recomputed — any manual layout and manually routed relationships will be discarded. A checkpoint is created in the history beforehand, and the action can also be undone with Ctrl+Z.',
  'canvas.autoLayout.confirmAction': 'Recompute layout',
  'canvas.fk.resetRoute': 'Reset route',
  'history.checkpoint.beforeAutoLayout': 'Before auto-layout',

  // Epic 2 — synchronization with the DB.
  'sync.button': 'Synchronize',
  'sync.title': 'Database synchronization',
  'sync.phase.computingDiff': 'Computing differences',
  'sync.noDifferences': 'The model matches the current state of the database.',
  'sync.selectAllAddedChanged': 'Select all added/changed',
  'sync.merge.action': 'Merge selected',
  'sync.applying': 'Merging selected changes…',
  'sync.done.baselineOnly': 'Nothing changed structurally, the baseline was updated.',
  'sync.done.merged': 'Merged items: {count}. The baseline was updated.',
  'sync.confirmRemoval.title': 'Remove the selected items from the model?',
  'sync.confirmRemoval.question':
    'Selected for removal: {count} items that no longer exist in the database. This cannot be undone with Ctrl+Z — you can only go back by restoring an older version from the history.',
  'sync.confirmRemoval.tableInGroup': 'Table "{name}" is a member of group "{group}" — it will be removed from it.',
  'sync.confirmRemoval.tableHasNotes': 'Table "{name}" has {count} attached note(s) — they will be deleted.',
  'sync.confirmRemoval.hint': 'Other manual canvas edits (positions, colors) are left untouched by the merge.',
  'history.checkpoint.manualSync': 'Manual synchronization',

  // Epic 9 — change script generation.
  'changeScript.button': 'Generate change script',
  'changeScript.title': 'Change script',
  'changeScript.noBaseline': 'The project does not have a baseline (last known DB state) yet.',
  'changeScript.lastSync': 'Last sync: {time}',
  'changeScript.lastSyncNever': 'No sync with the database has happened yet.',
  'changeScript.conflicts.intro': 'Found conflicts: {count}. For each, choose whether to accept the current database state or keep your change.',
  'changeScript.conflicts.baseline': 'Last known state',
  'changeScript.conflicts.db': 'Currently in the database',
  'changeScript.conflicts.model': 'Currently in the model',
  'changeScript.conflicts.acceptDb': 'Accept DB state',
  'changeScript.conflicts.keepMine': 'Keep my change',
  'changeScript.conflicts.continue': 'Continue',
  'changeScript.preflight.continue': 'Merge and continue',
  'changeScript.preflight.baselineLabel': 'Pre-flight before generating a change script',
  'changeScript.finalDiff.empty': 'The model matches the last known database state — nothing to generate.',
  'changeScript.generateButton': 'Generate script',
  'changeScript.generating': 'Generating script…',
  'changeScript.result.summary': 'Script generated — statements: {count}.',
  'changeScript.result.copy': 'Copy',
  'changeScript.result.download': 'Download .sql',
  'changeScript.result.ranIt': 'I ran the script → synchronize',
  'history.trigger.changeScriptGenerated.viewScript': 'View script',
  'history.changeScript.title': 'Generated change script',

  // Epic 12 — export to static HTML documentation.
  'export.button': 'Export to HTML',
  'export.documentTitle': '{name} — schema documentation',
  'export.generatedAt': 'Generated {time}',
  'export.diagram.title': 'Diagram',
  'export.documentation.title': 'Table documentation',
  'export.freeNotes.title': 'Free-standing notes',
  'export.notes.fromGroup': 'Note on group "{name}"',
  'export.index.unique': 'unique',
  'export.column.header.name': 'Column',
  'export.column.header.type': 'Type',
  'export.column.header.nullable': 'Nullable',
  'export.column.header.default': 'Default value',
  'export.column.header.key': 'Key',
  'export.column.header.comment': 'DB comment',
  'export.column.header.notes': 'Notes',
  'export.column.key.primary': 'PK',
  'export.column.key.foreign': 'FK',
  'export.column.key.both': 'PK, FK',

  'canvas.group.createFromSelection': 'Group',
  'canvas.group.createTitle': 'New group',
  'canvas.group.renameTitle': 'Rename group',
  'canvas.group.rename': 'Rename',
  'canvas.group.renameHint': 'Double-click to rename the group',
  'canvas.group.dissolve': 'Dissolve group',
  'canvas.group.removeTable': 'Remove from group',
  'canvas.group.addTable': 'Add to group “{name}”',
  'canvas.group.nameLabel': 'Group name',
  'canvas.group.defaultName': 'New group',

  'canvas.note.addToTable': 'Add note to table',
  'canvas.note.addToColumn': 'Add note to column',
  'canvas.note.addToGroup': 'Add note to group',
  'canvas.note.addFree': 'Add note',
  'canvas.note.unpin': 'Unpin',
  'canvas.note.pinTo': 'Pin to…',
  'canvas.note.pinToTable': 'Pin to table “{name}”',
  'canvas.note.pinToGroup': 'Pin to group “{name}”',
  'canvas.note.countTooltip': 'Notes: {count}',
  'canvas.note.placeholder': 'Note text…',

  // Epic 5 — visual FK relationship editor (drag&drop between canvas columns).
  'canvas.fk.typeMismatch': 'Can\'t create the relationship: column "{source}" and "{target}" have incompatible types.',
  'canvas.fk.compositeReconnectUnsupported':
    'A composite relationship (multiple column pairs) can only be reconnected via the inspector form.',
  'canvas.fk.deleteEdge': 'Delete relationship',

  'canvas.table.colorButton': 'Table colour',

  'canvas.column.nullable': 'NULL',
  'canvas.column.notNullable': 'NOT NULL',

  // Epic 8 — switching views (DB schema / ER diagram).
  'canvas.viewSettings.button': 'Display',
  'canvas.viewSettings.presetDbSchema': 'DB schema view',
  'canvas.viewSettings.presetErDiagram': 'ER diagram view',
  'canvas.viewSettings.onlyKeyColumns': 'Show only key columns',
  'canvas.viewSettings.showColumnType': 'Column data type',
  'canvas.viewSettings.showNullable': 'Nullable indicator',
  'canvas.viewSettings.showDefault': 'Default value',
  'canvas.viewSettings.showComment': 'Column comment',
  'canvas.viewSettings.showCardinalityLabel': 'Cardinality label on relationship',

  'canvas.color.none': 'No colour',
  'canvas.color.slate': 'Slate',
  'canvas.color.red': 'Red',
  'canvas.color.orange': 'Orange',
  'canvas.color.amber': 'Amber',
  'canvas.color.green': 'Green',
  'canvas.color.teal': 'Teal',
  'canvas.color.blue': 'Blue',
  'canvas.color.indigo': 'Indigo',
  'canvas.color.violet': 'Violet',
  'canvas.color.pink': 'Pink',

  'canvas.command.moveTable': 'move table',
  'canvas.command.moveGroup': 'move group',
  'canvas.command.recolorTable': 'change table colour',
  'canvas.command.createGroup': 'create group “{name}”',
  'canvas.command.renameGroup': 'rename group to “{name}”',
  'canvas.command.dissolveGroup': 'dissolve group “{name}”',
  'canvas.command.addTableToGroup': 'add table to group',
  'canvas.command.removeTableFromGroup': 'remove table from group',
  'canvas.command.createNote': 'create note',
  'canvas.command.editNoteText': 'edit note text',
  'canvas.command.moveNote': 'move note',
  'canvas.command.deleteNote': 'delete note',
  'canvas.command.pinNote': 'change note attachment',
  'canvas.command.clearTableCanvasState': 'clean up deleted table canvas metadata',
  'canvas.command.autoLayout': 'automatic auto-layout',
  'canvas.command.placeTable': 'automatic table placement',
  'canvas.command.updateEdgeRoute': 'update relationship route',
  'canvas.command.resetEdgeRoute': 'reset relationship route',

  'theme.toggleToLight': 'Switch to light mode',
  'theme.toggleToDark': 'Switch to dark mode',
  'language.switchTo': 'Switch language to {language}',
  'language.cs': 'Czech',
  'language.en': 'English',

  'workspace.tree.title': 'Navigation',
  'workspace.tree.searchPlaceholder': 'Search table or column…',
  'workspace.tree.empty': 'The schema has no tables yet.',
  'workspace.tree.noMatches': 'Nothing matches “{query}”.',
  'workspace.tree.columnsTooltip': 'Columns: {count}',
  'workspace.panel.collapse': 'Collapse panel',
  'workspace.panel.expand': 'Expand panel',

  'inspector.title': 'Properties',
  'inspector.empty': 'Select a table, column, group or note to see its detail.',
  'inspector.section.comment': 'Database comment',
  'inspector.section.notes': 'Notes',
  'inspector.comment.empty': 'No comment.',
  'inspector.notes.empty': 'No notes yet.',
  'inspector.notes.add': 'Add note',
  'inspector.table.title': 'Table',
  'inspector.table.schema': 'Schema',
  'inspector.table.nameLabel': 'Table name',
  'inspector.table.duplicateName': 'The name “{name}” already exists in this schema.',
  'inspector.table.columnsTitle': 'Columns ({count})',
  'inspector.table.addColumn': 'Add column',
  'inspector.table.detailTitle': 'Constraints and indexes',
  'inspector.table.primaryKey': 'Primary key',
  'inspector.table.foreignKeys': 'Foreign keys',
  'inspector.table.addForeignKey': 'Add foreign key',
  'inspector.table.uniqueConstraints': 'Unique constraints',
  'inspector.table.uniqueTitle': 'Unique constraints ({count})',
  'inspector.table.addUnique': 'Add unique constraint',
  'inspector.table.checkConstraints': 'Check constraints',
  'inspector.table.checkTitle': 'Check constraints ({count})',
  'inspector.table.addCheck': 'Add check constraint',
  'inspector.table.indexes': 'Indexes',
  'inspector.table.addIndex': 'Add index',
  'inspector.table.edit': 'Edit',
  'inspector.table.none': '—',
  'inspector.table.deleteButton': 'Delete table',
  'inspector.table.deleteConfirmTitle': 'Delete table “{name}”?',
  'inspector.table.deleteConfirmBlocked':
    'This table cannot be deleted — it is referenced by foreign keys: {list}. Remove or redirect them first.',
  'inspector.table.deleteConfirmQuestion': 'The table and its canvas metadata (position, colour) will be removed.',
  'inspector.table.deleteConfirmWarningGroup': 'The table is a member of group “{name}” — it will be removed from it.',
  'inspector.table.deleteConfirmWarningNotes': 'Notes pinned to the table or its columns: {count} — they will be deleted.',
  'inspector.column.title': 'Column',
  'inspector.column.nameLabel': 'Column name',
  'inspector.column.duplicateName': 'The name “{name}” already exists in this table.',
  'inspector.column.type': 'Type',
  'inspector.column.typeCustom': 'Custom / unknown type',
  'inspector.column.typeCustomPlaceholder': 'e.g. varchar(255)',
  'inspector.column.typeParamLength': 'Length',
  'inspector.column.typeParamPrecision': 'Precision',
  'inspector.column.typeParamScale': 'Scale',
  'inspector.column.rawTypeHint': 'DB type: {rawType}',
  'inspector.column.nullable': 'Nullable',
  'inspector.column.default': 'Default value',
  'inspector.column.isPrimaryKey': 'Part of primary key',
  'inspector.column.isForeignKey': 'Part of foreign key',
  'inspector.column.deleteButton': 'Delete column',
  'inspector.column.deleteConfirmTitle': 'Delete column “{name}”?',
  'inspector.column.deleteBlocked': 'The column is part of foreign key “{name}” — edit or delete it first.',
  'inspector.column.deletePkWarning':
    'This is the only primary key column — the table will be left without a PK after deletion.',
  // Epic 5 — detail of the FK relationship selected on the canvas (clicking an edge).
  'inspector.foreignKey.columns': 'Columns',
  'inspector.foreignKey.references': 'References',
  'inspector.foreignKey.onDelete': 'ON DELETE',
  'inspector.foreignKey.onUpdate': 'ON UPDATE',
  'inspector.foreignKey.cardinality': 'Cardinality',
  'inspector.foreignKey.editCardinalityShortcut': 'Edit column unique constraint',

  'inspector.group.title': 'Group',
  'inspector.group.nameLabel': 'Group name',
  'inspector.group.membersTitle': 'Tables in group ({count})',
  'inspector.note.title': 'Note',
  'inspector.note.attachedToTable': 'Pinned to table “{name}”',
  'inspector.note.attachedToColumn': 'Pinned to column “{name}”',
  'inspector.note.attachedToGroup': 'Pinned to group “{name}”',
  'inspector.note.free': 'Free-floating note',

  'schema.validation.emptyName': 'The name must not be empty.',
  'schema.table.addAction': 'Add table',

  'schema.type.category.numeric': 'Numeric',
  'schema.type.category.text': 'Text',
  'schema.type.category.binary': 'Binary',
  'schema.type.category.boolean': 'Boolean',
  'schema.type.category.datetime': 'Date and time',
  'schema.type.category.uuid': 'UUID',
  'schema.type.category.structured': 'Structured',
  'schema.type.category.network': 'Network',
  'schema.type.category.spatial': 'Spatial',
  'schema.type.category.other': 'Other',
  'schema.type.reason.empty': 'The data type must not be empty.',
  'schema.type.reason.unknownType': 'Unknown type “{detail}”.',
  'schema.type.reason.parameterRequired': 'Type “{detail}” requires a parameter.',
  'schema.type.reason.parameterNotAllowed': 'Type “{detail}” does not accept parameters.',
  'schema.type.reason.parameterInvalid': 'Type “{detail}” has invalid parameters.',

  'schema.command.addTable': 'add table “{name}”',
  'schema.command.deleteTable': 'delete table “{name}”',
  'schema.command.renameTable': 'rename table “{from}” to “{to}”',
  'schema.command.updateTableComment': 'update table comment',
  'schema.command.addColumn': 'add column “{name}”',
  'schema.command.deleteColumn': 'delete column “{name}”',
  'schema.command.moveColumn': 'move column “{name}”',
  'schema.command.renameColumn': 'rename column “{from}” to “{to}”',
  'schema.command.updateColumn': 'update column',
  'schema.command.setPrimaryKey': 'update primary key',
  'schema.command.addUniqueConstraint': 'add unique constraint “{name}”',
  'schema.command.updateUniqueConstraint': 'update unique constraint “{name}”',
  'schema.command.deleteUniqueConstraint': 'delete unique constraint “{name}”',
  'schema.command.addCheckConstraint': 'add check constraint “{name}”',
  'schema.command.updateCheckConstraint': 'update check constraint “{name}”',
  'schema.command.deleteCheckConstraint': 'delete check constraint “{name}”',
  'schema.command.addForeignKey': 'add foreign key “{name}”',
  'schema.command.updateForeignKey': 'update foreign key “{name}”',
  'schema.command.deleteForeignKey': 'delete foreign key “{name}”',
  'schema.command.addIndex': 'add index “{name}”',
  'schema.command.updateIndex': 'update index “{name}”',
  'schema.command.deleteIndex': 'delete index “{name}”',

  'schema.dialog.unique.createTitle': 'New unique constraint',
  'schema.dialog.unique.editTitle': 'Edit unique constraint',
  'schema.dialog.unique.nameLabel': 'Name',
  'schema.dialog.unique.columnsLabel': 'Columns',
  'schema.dialog.unique.columnsRequired': 'Select at least one column.',

  'schema.dialog.check.createTitle': 'New check constraint',
  'schema.dialog.check.editTitle': 'Edit check constraint',
  'schema.dialog.check.nameLabel': 'Name',
  'schema.dialog.check.expressionLabel': 'Condition expression',
  'schema.dialog.check.expressionEmpty': 'The expression must not be empty.',
  'schema.dialog.check.expressionUnbalanced': 'The parentheses in the expression are not balanced.',

  'schema.dialog.index.createTitle': 'New index',
  'schema.dialog.index.editTitle': 'Edit index',
  'schema.dialog.index.nameLabel': 'Name',
  'schema.dialog.index.columnsLabel': 'Columns (in order)',
  'schema.dialog.index.uniqueLabel': 'Unique',
  'schema.dialog.index.addColumn': 'Add column',
  'schema.dialog.index.columnsRequired': 'Select at least one column.',
  'schema.dialog.index.remove': 'Remove',
  'schema.dialog.index.moveUp': 'Move up',
  'schema.dialog.index.moveDown': 'Move down',

  'schema.dialog.fk.createTitle': 'New foreign key',
  'schema.dialog.fk.editTitle': 'Edit foreign key',
  'schema.dialog.fk.nameLabel': 'Name',
  'schema.dialog.fk.targetTableLabel': 'Target table',
  'schema.dialog.fk.onDeleteLabel': 'ON DELETE',
  'schema.dialog.fk.onUpdateLabel': 'ON UPDATE',
  'schema.dialog.fk.pairsLabel': 'Columns (source → target)',
  'schema.dialog.fk.sourceColumnLabel': 'Source column',
  'schema.dialog.fk.targetColumnLabel': 'Target column',
  'schema.dialog.fk.addPair': 'Add column pair',
  'schema.dialog.fk.removePair': 'Remove',
  'schema.dialog.fk.pairsRequired': 'Add at least one column pair.',
  'schema.dialog.fk.duplicateSourceColumn': 'Source column “{name}” is used more than once.',
  'schema.dialog.fk.incompleteRow': 'Select both a source and a target column for each pair.',
  'schema.dialog.fk.typeMismatch':
    'Source column “{source}” ({sourceType}) and target “{target}” ({targetType}) do not have compatible types.',
  'schema.dialog.fk.targetNotKeyWarning':
    'The target columns are not part of the target table’s primary key or a unique constraint — the relation will still be created, but double-check this is intended.',

  'history.openButton': 'History',
  'history.title': 'Version history',
  'history.createCheckpoint': 'Create checkpoint',
  'history.checkpointLabelPlaceholder': 'Label (optional)',
  'history.empty': 'The history has no entries yet.',
  'history.column.trigger': 'Type',
  'history.column.time': 'Time',
  'history.column.author': 'Author',
  'history.column.label': 'Label',
  'history.column.tables': 'Tables',
  'history.column.actions': 'Actions',
  'history.trigger.checkpoint': 'Checkpoint',
  'history.trigger.dbSync': 'DB sync',
  'history.trigger.changeScriptGenerated': 'Script generated',
  'history.trigger.revert': 'Version restored',
  'history.revert.action': 'Restore',
  'history.revert.confirmTitle': 'Restore this version?',
  'history.revert.confirmQuestion':
    'The current state of the model (structure and canvas) will be overwritten with the entry from {time}.',
  'history.revert.confirmHint':
    'Nothing in the history is deleted — a new “Version restored” entry is appended at the end.',
  'history.revert.confirm': 'Restore version',
  'history.diff.title': 'Compare versions',
  'history.diff.from': 'Version A',
  'history.diff.to': 'Version B',
  'history.diff.show': 'Show diff',
  'history.diff.empty': 'There is no structural difference between the selected versions.',
  'history.diff.column.object': 'Object',
  'history.diff.column.path': 'Path',
  'history.diff.column.old': 'Previous value',
  'history.diff.column.new': 'New value',
  'history.diff.changeType.added': 'added',
  'history.diff.changeType.changed': 'changed',
  'history.diff.changeType.removed': 'removed',
  'history.diff.changeType.renamed': 'renamed',
  'history.diff.objectType.table': 'table',
  'history.diff.objectType.column': 'column',
  'history.diff.objectType.primaryKey': 'primary key',
  'history.diff.objectType.foreignKey': 'foreign key',
  'history.diff.objectType.uniqueConstraint': 'unique constraint',
  'history.diff.objectType.checkConstraint': 'check constraint',
  'history.diff.objectType.index': 'index',

  'error.validationFailed': 'The submitted data is not valid.',
  'error.notFound': 'The requested item does not exist.',
  'error.connectionFailed': 'Could not connect to the database.',
  'error.importFailed': 'The import failed.',
  'error.syncFailed': 'Database synchronization failed.',
  'error.unsupportedDialect': 'The selected SQL dialect is not supported.',
  'error.fileNotFound': 'The file does not exist or is not readable.',
  'error.pathNotAllowed': 'The path points outside the allowed directory.',
  'error.notImplemented': 'This feature is not implemented yet.',
  'error.internalError': 'Server-side error.',
  'error.unauthorized': 'You need to sign in.',
  'error.invalidCredentials': 'Invalid username or password.',
  'error.forbidden': 'You are not allowed to perform this action.',
  'error.usernameTaken': 'That username is already taken.',
  'error.editLeaseHeld': 'Another user is currently editing this project.',

  // Epic 14 — users and permissions.
  'auth.login.title': 'Sign in',
  'auth.login.usernameLabel': 'Username',
  'auth.login.passwordLabel': 'Password',
  'auth.login.submit': 'Sign in',
  'auth.logout': 'Sign out',
  'auth.loggedInAs': 'Signed in as {name}',

  'users.button': 'Users',
  'users.title': 'Users',
  'users.column.username': 'Username',
  'users.column.displayName': 'Display name',
  'users.create.title': 'New user',
  'users.create.usernameLabel': 'Username',
  'users.create.displayNameLabel': 'Display name',
  'users.create.passwordLabel': 'Password',
  'users.create.submit': 'Create user',
  'users.create.success': 'User "{name}" was created.',

  'password.change.button': 'Change password',
  'password.change.title': 'Change password',
  'password.change.currentLabel': 'Current password',
  'password.change.newLabel': 'New password',
  'password.change.submit': 'Change password',
  'password.change.success': 'Password changed.',

  'project.editors.title': 'Editors',
  'project.editors.creatorLabel': 'Creator: {name}',
  'project.editors.empty': 'This project has no editors yet.',
  'project.editors.addPlaceholder': 'Pick a user…',
  'project.editors.add': 'Add editor',
  'project.editors.remove': 'Remove',
  'project.editors.transferButton': 'Transfer ownership',
  'project.editors.transferTitle': 'Transfer project ownership?',
  'project.editors.transferQuestion':
    'Ownership of project "{project}" will transfer to "{user}". You will become an editor and lose the right to delete the project or manage editors and ownership.',
  'project.editors.transferConfirm': 'Transfer ownership',
  'project.editors.transferNoEditors':
    'Ownership can only be transferred to an existing editor — add someone as an editor first.',
  'project.role.creator': 'Creator',
  'project.role.editor': 'Editor',

  'lease.readOnlyBanner': '"{name}" is currently editing (last activity {seconds}s ago) — the project is view-only.',
  'lease.takeoverButton': 'Take over editing',
  'lease.takenOverNotice': '"{name}" took over editing — you cannot save until you claim it back.',

  // Epic 15 — extended project capabilities.
  'project.rename.nameLabel': 'Project name',
  'project.rename.save': 'Save name',
  'project.rename.saved': 'Project name saved.',

  'canvas.tools.button': 'Tools',

  'createScript.button': 'Generate create script',
  'createScript.title': 'Clean create script',
  'createScript.description':
    'Generates a complete create script for the whole current model — as if it were going into an entirely empty database. Ignores the last baseline and any previous change scripts.',
  'createScript.schemaNameLabel': 'Custom target schema name (optional)',
  'createScript.schemaNameHint':
    'Only available for projects with a single schema — a project with multiple schemas generates the script with the original names.',
  'createScript.generateButton': 'Generate',
  'createScript.generating': 'Generating script…',
  'createScript.empty': 'The model does not contain any objects to generate yet.',
  'createScript.result.summary': 'Script generated — statements: {count}.',
  'createScript.result.copy': 'Copy',
  'createScript.result.download': 'Download .sql',

  'projects.duplicate.button': 'Duplicate',
  'projects.duplicate.title': 'Duplicate project',
  'projects.duplicate.nameLabel': 'Copy name',
  'projects.duplicate.defaultName': '{name} (copy)',
  'projects.duplicate.hint':
    'The copy is created as an independent project — without the original’s version history and without a database connection. A connection can be attached later, same as for an empty project.',
  'projects.duplicate.confirm': 'Create copy',

  'inspector.column.moveUp': 'Move up',
  'inspector.column.moveDown': 'Move down',
};
